import { useMemo } from "react";
import { CategoryResponse } from "../typings/interfaces/responses/category-response";
import { PricingResponse } from "../typings/interfaces/responses/pricing-response";
import { useAppSelector } from "./reduxHooks";

// Pseudo service id used by the POS "Alteration" tab
export const ALTERATION_SERVICE_ID = -100000;

// Every "...Alteration..." category comes back from GET /price, so without this
// a Dry Clean item could pick up Household or Costume alteration prices.
// `serviceIds` are candidates in priority order; the pseudo Alteration id is
// skipped because a new item started from the Alteration tab carries it.
export const scopeAlterationPricing = (
  serviceIds: Array<number | undefined>,
  categories: Array<CategoryResponse> | undefined,
  alterationPrices: Array<PricingResponse> | null | undefined,
  garmentId?: number
) => {
  const serviceId = serviceIds.find(
    (id) => id !== undefined && id !== ALTERATION_SERVICE_ID
  );
  const alterationCategoryId = categories?.find(
    (category) => category.id === serviceId
  )?.alterationCategoryId;

  const prices = alterationCategoryId
    ? (alterationPrices || []).filter(
        (item) => item.categoryId === alterationCategoryId
      )
    : [];
  // Only types with at least one option priced for the item's garment, so a
  // type can't lead to an empty option list
  const types = prices.filter((item) => garmentId === undefined || item.subCategoryId === garmentId).filter(
    (item, index, self) =>
      self.findIndex((e) => e.materialId === item.materialId) === index
  );
  return { alterationPrices: prices, alterationModificationType: types };
};

// The work options (Shorten Bottom, w/ Vent…) offered for one garment and
// alteration type: only those priced in the item's alteration category. The
// sub-category/material relationship table has no category or price, so it
// mixed in other services' and stale options that then priced at $0.
export const alterationOptionsFor = (
  alterationPrices: Array<PricingResponse>,
  garmentId: number | undefined,
  alterationTypeId: number | undefined
) =>
  alterationPrices
    .filter(
      (item) =>
        garmentId !== undefined &&
        alterationTypeId !== undefined &&
        item.subCategoryId === garmentId &&
        item.materialId === alterationTypeId
    )
    .map((item) => ({ id: item.secondSubCategoryId, name: item.secondSubCategoryName }))
    .filter((option, index, self) => self.findIndex((o) => o.id === option.id) === index);

// Alteration prices and types for the service the selected item belongs to.
const useAlterationPricing = () => {
  const { selectedCartItem, selectedServiceType, previousServiceType } =
    useAppSelector((state) => state.cart);
  const { categoryResponse, alterationPrices } = useAppSelector(
    (state) => state.pricing
  );
  const itemServiceId = selectedCartItem?.serviceType?.id;
  const selectedId = selectedServiceType?.id;
  const previousId = previousServiceType?.id;
  const garmentId = selectedCartItem?.garmentType?.id;

  return useMemo(
    () =>
      scopeAlterationPricing(
        [itemServiceId, selectedId, previousId],
        categoryResponse?.body as Array<CategoryResponse> | undefined,
        alterationPrices,
        garmentId
      ),
    [itemServiceId, selectedId, previousId, categoryResponse, alterationPrices, garmentId]
  );
};

export default useAlterationPricing;
